import React from 'react';

import { ActionButtonVariants } from 'components/action-button';
import LoadingAnimation from 'components/loading-animation';

import buttonColor from './button-color';
import {
  bottomServiceButtonId,
  bottomServiceCateringTimeButtonId,
  bottomServiceDetailsId,
  bottomServiceHeadingId,
} from './constants';
import { NextIcon } from './icons';
import {
  IconContainer,
  NextIconContainer,
  StyledActionButton,
  StyledBottomServiceContainer,
  StyledButtonsContainer,
  StyledDateTimeButtonText,
  StyledDetailsButton,
  StyledDetailsText,
  StyledHeading,
  StyledInnerWrapper,
} from './styled';
import { BottomServiceVariants, IBottomServiceModeViewProps } from './types';

export const BottomServiceModeRedesignView = React.forwardRef<
  HTMLDivElement,
  IBottomServiceModeViewProps
>(
  (
    {
      isLoading = false,
      variant = BottomServiceVariants.DEFAULT,
      heading,
      details,
      icon,
      buttonBadgeContentText,
      buttonContentIcon,
      buttonContentText,
      buttonContentTextLabel,
      onBottomServiceClick = () => {},
      onButtonClick = () => {},
      buttonDisabled = false,
      className = '',
      onCateringTimeButtonClick = () => undefined,
      cateringTimeButtonLabel = '',
      cateringTimeButtonDisabled = false,
      cateringTimeButtonIcon,
    },
    ref
  ) => {
    const showCateringTimeButton = Boolean(cateringTimeButtonLabel);
    const showCheckoutButton = Boolean(buttonContentText);
    const color = buttonColor(variant, true);

    const handleCartClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      onButtonClick(e);
    };

    if (isLoading) {
      return (
        <StyledBottomServiceContainer variant={variant} className={className} ref={ref}>
          <LoadingAnimation fillColor={Styles.color.black} />
        </StyledBottomServiceContainer>
      );
    }

    return (
      <StyledBottomServiceContainer
        variant={variant}
        className={className}
        ref={ref}
        data-testid="bottom-service-redesign"
      >
        <StyledInnerWrapper>
          <StyledDetailsButton
            variant={variant}
            onClick={onBottomServiceClick}
            data-testid="bottom-service-details-button"
            aria-labelledby={`${bottomServiceHeadingId} ${bottomServiceDetailsId}`}
          >
            <IconContainer>{icon}</IconContainer>
            <StyledHeading id={bottomServiceHeadingId} variant={variant}>
              {heading}
            </StyledHeading>
            <StyledDetailsText
              id={bottomServiceDetailsId}
              variant={variant}
              data-testid="bottom-service-address"
              data-private
            >
              {details}
            </StyledDetailsText>
            <NextIconContainer>
              <NextIcon />
            </NextIconContainer>
          </StyledDetailsButton>
          {(showCheckoutButton || showCateringTimeButton) && (
            <StyledButtonsContainer $badgeContent={buttonBadgeContentText} variant={variant}>
              {showCateringTimeButton && (
                <StyledActionButton
                  onClick={onCateringTimeButtonClick}
                  variant={ActionButtonVariants.OUTLINE}
                  color={color}
                  data-testid={bottomServiceCateringTimeButtonId}
                  disabled={cateringTimeButtonDisabled}
                  startIcon={cateringTimeButtonIcon}
                  aria-label={cateringTimeButtonLabel}
                >
                  <StyledDateTimeButtonText>{cateringTimeButtonLabel}</StyledDateTimeButtonText>
                </StyledActionButton>
              )}

              {showCheckoutButton && (
                <StyledActionButton
                  onClick={handleCartClick}
                  variant={ActionButtonVariants.PRIMARY}
                  color={color}
                  data-testid={bottomServiceButtonId}
                  disabled={buttonDisabled}
                  startIcon={buttonContentIcon}
                  aria-label={buttonContentTextLabel || buttonContentText}
                >
                  {buttonContentText}
                </StyledActionButton>
              )}
            </StyledButtonsContainer>
          )}
        </StyledInnerWrapper>
      </StyledBottomServiceContainer>
    );
  }
);
